import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import aiPropertySearchService from '../../api/services/aiPropertySearch';
import type { PropertyMatch } from '../../api/services/aiPropertySearch';

interface BuyerPropertyMatchesState {
  matches: PropertyMatch[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  unviewedCount: number;
}

const initialState: BuyerPropertyMatchesState = {
  matches: [],
  loading: false,
  error: null,
  totalCount: 0,
  unviewedCount: 0,
};

// Thunks
export const fetchBuyerPropertyMatches = createAsyncThunk(
  'buyerPropertyMatches/fetchMatches',
  async (limit: number = 50) => {
    const matches = await aiPropertySearchService.getMatches(limit);
    return matches;
  }
);

export const updateMatchStatus = createAsyncThunk(
  'buyerPropertyMatches/updateStatus',
  async ({ id, updates }: {
    id: string;
    updates: { isViewed?: boolean; isSaved?: boolean; isDismissed?: boolean };
  }) => {
    const updatedMatch = await aiPropertySearchService.updateMatch(id, updates);
    return updatedMatch;
  }
);

export const dismissMatch = createAsyncThunk(
  'buyerPropertyMatches/dismiss',
  async (id: string) => {
    const updatedMatch = await aiPropertySearchService.dismissMatch(id);
    return updatedMatch;
  }
);

export const saveMatch = createAsyncThunk(
  'buyerPropertyMatches/save',
  async (id: string) => {
    const updatedMatch = await aiPropertySearchService.saveMatch(id);
    return updatedMatch;
  }
);

export const markMatchViewed = createAsyncThunk(
  'buyerPropertyMatches/markViewed',
  async (id: string) => {
    const updatedMatch = await aiPropertySearchService.markViewed(id);
    return updatedMatch;
  }
);

// Slice
const buyerPropertyMatchesSlice = createSlice({
  name: 'buyerPropertyMatches',
  initialState,
  reducers: {
    clearMatches: (state) => {
      state.matches = [];
      state.totalCount = 0;
      state.unviewedCount = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch matches
    builder
      .addCase(fetchBuyerPropertyMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyerPropertyMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.matches = action.payload;
        state.totalCount = action.payload.length;
        state.unviewedCount = action.payload.filter(m => !m.isViewed).length;
      })
      .addCase(fetchBuyerPropertyMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch matches';
      })
      // Update match status
      .addCase(updateMatchStatus.fulfilled, (state, action) => {
        const index = state.matches.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.matches[index] = action.payload;
          state.unviewedCount = state.matches.filter(m => !m.isViewed).length;
        }
      })
      // Dismiss match
      .addCase(dismissMatch.fulfilled, (state, action) => {
        state.matches = state.matches.filter(m => m.id !== action.payload.id);
        state.totalCount = state.matches.length;
        state.unviewedCount = state.matches.filter(m => !m.isViewed).length;
      })
      // Save match
      .addCase(saveMatch.fulfilled, (state, action) => {
        const index = state.matches.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.matches[index] = action.payload;
        }
      })
      // Mark viewed
      .addCase(markMatchViewed.fulfilled, (state, action) => {
        const index = state.matches.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.matches[index] = action.payload;
          state.unviewedCount = state.matches.filter(m => !m.isViewed).length;
        }
      });
  },
});

export const { clearMatches } = buyerPropertyMatchesSlice.actions;
export default buyerPropertyMatchesSlice.reducer;